import React from "react";
import { Link, useLocation } from "react-router-dom";

const Icon = ({ children }) => (
    <span
        style={{
            display: "inline-flex",
            width: 20,
            height: 20,
            alignItems: "center",
            justifyContent: "center",
            marginRight: 8,
        }}
    >
        {children}
    </span>
);

const HeaderSelector = () => {
    const location = useLocation();
    const currentView =
        location.pathname === "/messages" ? "messages" : "devices";

    // Check if we were using uc filter in the last session
    const wasUsingUcFilter = sessionStorage.getItem("usingUcFilter") === "true";
    const searchParams = new URLSearchParams(location.search);
    const currentUcParam = searchParams.get("uc");
    const displayMode = searchParams.get("display") === "1";
    const storedUcParam = sessionStorage.getItem("lastUcFilter");
    const storedCollection = sessionStorage.getItem("lastSelectedCollection");

    let messagesPath = "/messages";

    // Set flag to indicate we're restoring from devices
    const handleMessagesClick = () => {
        sessionStorage.setItem("restoringFromDevices", "true");
    };

    // Determine what URL to build based on the context
    if (currentUcParam) {
        // If current URL has uc, preserve it
        messagesPath += `?uc=${currentUcParam}`;
        if (
            storedCollection &&
            storedCollection.toLowerCase().includes(currentUcParam.toLowerCase())
        ) {
            messagesPath += `&collection=${storedCollection}`;
        }
    } else if (wasUsingUcFilter && storedUcParam) {
        // If we were using uc filter before, restore it
        messagesPath += `?uc=${storedUcParam}`;
        if (
            storedCollection &&
            storedCollection.toLowerCase().includes(storedUcParam.toLowerCase())
        ) {
            messagesPath += `&collection=${storedCollection}`;
        }
    } else if (storedCollection && !wasUsingUcFilter) {
        // If we were NOT using uc filter, just restore collection
        messagesPath += `?collection=${storedCollection}`;
    }

    if (displayMode && !messagesPath.includes("display=1")) {
        messagesPath += messagesPath.includes("?") ? "&display=1" : "?display=1";
    }

    const displayToggleParams = new URLSearchParams(location.search);
    if (displayMode) {
        displayToggleParams.delete("display");
    } else {
        displayToggleParams.set("display", "1");
    }
    const displayToggleSearch = displayToggleParams.toString();
    const displayTogglePath = `${location.pathname}${
        displayToggleSearch ? `?${displayToggleSearch}` : ""
    }`;

    const navBtn = (label, path, key) => {
        const active = currentView === key;
        return (
            <Link
                to={path}
                aria-pressed={active}
                onClick={key === "messages" ? handleMessagesClick : undefined}
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                    borderRadius: 9999,
                    border: "1px solid var(--color-lightgrey)",
                    background: active
                        ? "var(--color-secondary)"
                        : displayMode
                          ? "#111827"
                          : "transparent",
                    color: active
                        ? "var(--color-white)"
                        : displayMode
                          ? "#d7def0"
                          : "var(--color-background)",
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: 13,
                    transition: "all 160ms ease-in-out",
                    textDecoration: "none",
                }}
            >
                <Icon>
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M3 7h18M3 12h18M3 17h18"
                            stroke={
                                active
                                    ? "var(--color-white)"
                                    : displayMode
                                      ? "#d7def0"
                                      : "var(--color-background)"
                            }
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </Icon>
                {label}
            </Link>
        );
    };

    return (
        <header
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 20px",
                borderBottom: "1px solid var(--color-lightgrey)",
                background: displayMode ? "#080b13" : "var(--color-white)",
                color: displayMode ? "#d7def0" : "var(--color-background)",
            }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <div
                        style={{
                            width: 44,
                            height: 44,
                            borderRadius: 10,
                            background: "var(--color-secondary)",
                            color: "var(--color-white)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 800,
                            marginRight: 12,
                        }}
                    >
                        DF
                    </div>
                    <div>
                        <div
                            style={{
                                fontSize: 16,
                                fontWeight: 800,
                                letterSpacing: "-0.02em",
                                color: displayMode
                                    ? "#f8fafc"
                                    : "var(--color-background)",
                            }}
                        >
                            DataFlow
                        </div>
                    </div>
                </div>

                <nav style={{ display: "flex", gap: 10 }}>
                    {navBtn("Devices", "/devices", "devices")}
                    {navBtn("Messages", messagesPath, "messages")}
                </nav>
            </div>

            <Link
                to={displayTogglePath}
                aria-pressed={displayMode}
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "9px 14px",
                    borderRadius: 9999,
                    border: displayMode
                        ? "1px solid #60a5fa"
                        : "1px solid var(--color-lightgrey)",
                    background: displayMode ? "#1d4ed8" : "transparent",
                    color: displayMode ? "#f8fafc" : "var(--color-background)",
                    cursor: "pointer",
                    fontWeight: 800,
                    fontSize: 13,
                    textDecoration: "none",
                    boxShadow: displayMode
                        ? "0 10px 30px rgba(37, 99, 235, 0.3)"
                        : "none",
                }}
                title={
                    displayMode
                        ? "Disable high contrast display mode"
                        : "Enable high contrast display mode"
                }
            >
                <span
                    style={{
                        width: 8,
                        height: 8,
                        borderRadius: 9999,
                        background: displayMode ? "#bfdbfe" : "#94a3b8",
                    }}
                />
                {displayMode ? "Display On" : "Display Mode"}
            </Link>
        </header>
    );
};

export default HeaderSelector;
